import { Text } from "@clipboard-health/ui-components";
import { Stack, type SxProps, type Theme } from "@mui/material";
import { type EmergencyCreditStatsCommon } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { type ReactElement } from "react";

import { getCreditProgressBarLabel } from "../utils/getCreditProgressBarLabel";
import { CreditProgressBar } from "./CreditProgressBar";

export interface LatenessCreditProgressBarProps {
  latenessCreditStats: EmergencyCreditStatsCommon;
  /**
   * Whether to show the "X/Y shifts complete" text below the progress bar
   * @default true
   */
  showShiftsProgress?: boolean;
  /**
   * Custom sx prop for the progress bar
   */
  sx?: SxProps<Theme>;
}

export function LatenessCreditProgressBar(props: LatenessCreditProgressBarProps): ReactElement {
  const { latenessCreditStats, showShiftsProgress = true, sx } = props;
  const { completedShiftsTowardsNextCredit, requiredShiftsForNextCredit, totalAvailableCredits } =
    latenessCreditStats;

  const hasCredit = totalAvailableCredits > 0;
  // If credit is available but no progress yet, show the bar as complete
  const isCreditAvailableWithNoProgress = hasCredit && completedShiftsTowardsNextCredit === 0;
  const completedCount = isCreditAvailableWithNoProgress
    ? requiredShiftsForNextCredit
    : completedShiftsTowardsNextCredit;

  return (
    <Stack spacing={1} alignItems="center">
      <CreditProgressBar
        completedCount={completedCount}
        requiredCount={requiredShiftsForNextCredit}
        label={getCreditProgressBarLabel("lateness", hasCredit)}
        color="green"
        sx={sx}
      />
      {showShiftsProgress && !isCreditAvailableWithNoProgress && (
        <Text semibold variant="caption" color={(theme) => theme.palette.text.tertiary}>
          {completedShiftsTowardsNextCredit}/{requiredShiftsForNextCredit} shifts complete
        </Text>
      )}
    </Stack>
  );
}
